// The Support Coordination calendar's date engine. Pure functions, all in the device's local time
// (the coordinator's own clock), so a 10am weekly visit stays 10am across a daylight-saving change.
// Tests: calendarMath.test.js
//
// An "occurrence" is what the grid draws: one appointment on one day, with start/end as Dates,
// `key` (unique on screen), `occurrence_date` (YYYY-MM-DD of its start) and `recurring`.

const DAY_MS = 86400000;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON = MONTHS.map((m) => m.slice(0, 3));
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad = (n) => String(n).padStart(2, '0');

export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[3]) ? d : null;
}
export const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
// Calendar arithmetic (setDate), so the wall-clock time survives a daylight-saving change.
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const addMinutes = (d, n) => new Date(d.getTime() + n * 60000);
export const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
// Weeks start on Monday, as Australian calendars do.
export function startOfWeek(d) {
  const x = startOfDay(d);
  return addDays(x, -((x.getDay() + 6) % 7));
}
export const minutesOfDay = (d) => d.getHours() * 60 + d.getMinutes();
export const snapMinutes = (m, step = 15) => Math.round(m / step) * step || 0;

// Six weeks from the Monday on or before the 1st (Google's month grid).
export function monthGrid(anchor) {
  const first = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

// [first day, last day] a view shows (both whole days, local).
export function rangeFor(view, anchor) {
  const a = startOfDay(anchor);
  if (view === 'day') return [a, a];
  if (view === 'week') { const s = startOfWeek(a); return [s, addDays(s, 6)]; }
  if (view === 'month') { const g = monthGrid(a); return [g[0], g[41]]; }
  if (view === '3day') return [a, addDays(a, 2)]; // phones: three days from the date
  return [a, addDays(a, 30)]; // schedule: the next 30 days
}
export function shiftAnchor(view, anchor, dir) {
  if (view === 'day') return addDays(anchor, dir);
  if (view === 'week') return addDays(anchor, 7 * dir);
  if (view === 'month') return new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1);
  if (view === '3day') return addDays(anchor, 3 * dir);
  return addDays(anchor, 30 * dir);
}
export function titleFor(view, anchor) {
  if (view === 'month') return `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
  if (view === 'day') return `${WEEKDAYS[anchor.getDay()]}, ${anchor.getDate()} ${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;
  const [s, e] = rangeFor(view, anchor);
  if (s.getFullYear() !== e.getFullYear()) return `${s.getDate()} ${MON[s.getMonth()]} ${s.getFullYear()} – ${e.getDate()} ${MON[e.getMonth()]} ${e.getFullYear()}`;
  if (s.getMonth() !== e.getMonth()) return `${s.getDate()} ${MON[s.getMonth()]} – ${e.getDate()} ${MON[e.getMonth()]} ${e.getFullYear()}`;
  return `${s.getDate()} – ${e.getDate()} ${MON[e.getMonth()]} ${e.getFullYear()}`;
}
// The same, short enough for a phone's toolbar.
export function shortTitleFor(view, anchor) {
  if (view === 'day') return `${WEEKDAYS[anchor.getDay()].slice(0, 3)} ${anchor.getDate()} ${MON[anchor.getMonth()]}`;
  const [s, e] = view === 'month' ? [anchor, anchor] : rangeFor(view, anchor);
  if (s.getFullYear() !== e.getFullYear()) return `${MON[s.getMonth()]} ${s.getFullYear()} – ${MON[e.getMonth()]} ${e.getFullYear()}`;
  if (s.getMonth() !== e.getMonth()) return `${MON[s.getMonth()]} – ${MON[e.getMonth()]} ${e.getFullYear()}`;
  return `${MON[s.getMonth()]} ${s.getFullYear()}`;
}
export const monthName = (i) => MONTHS[i];
export const shortMonth = (i) => MON[i];
export const weekdayName = (i) => WEEKDAYS[i];

// ── Repeating appointments ──────────────────────────────────────────────────────────────────
// The nth occurrence's start, or null when that step has no date (the 31st in a 30-day month).
function nthStart(first, repeat, n) {
  if (repeat === 'daily') return addDays(first, n);
  if (repeat === 'weekly') return addDays(first, 7 * n);
  if (repeat === 'fortnightly') return addDays(first, 14 * n);
  if (repeat === 'monthly') {
    const x = new Date(first.getFullYear(), first.getMonth() + n, first.getDate(), first.getHours(), first.getMinutes(), first.getSeconds());
    return x.getDate() === first.getDate() ? x : null;
  }
  return null;
}
const STEP_DAYS = { daily: 1, weekly: 7, fortnightly: 14, monthly: 28 };

function occurrence(appt, start, end, recurring) {
  const d = ymd(start);
  return { ...appt, start, end, occurrence_date: d, recurring, key: recurring ? `${appt.id}:${d}` : String(appt.id) };
}

// Every occurrence of one appointment that touches [from, to] (Dates; to is inclusive).
export function expandSeries(appt, from, to) {
  const first = new Date(appt.start_at);
  const dur = new Date(appt.end_at) - first;
  if (Number.isNaN(first.getTime()) || !(dur > 0)) return [];
  const touches = (s) => s <= to && s.getTime() + dur > from.getTime();
  const repeat = appt.repeat || 'none';
  if (repeat === 'none') return touches(first) ? [occurrence(appt, first, new Date(first.getTime() + dur), false)] : [];

  const until = appt.repeat_until ? parseYmd(String(appt.repeat_until).slice(0, 10)) : null;
  const untilEnd = until ? addDays(until, 1) : null;
  const skip = new Set((appt.exdates || []).map((x) => String(x).slice(0, 10)));
  const out = [];
  const keep = (s) => {
    if (untilEnd && s >= untilEnd) return false;
    if (!skip.has(ymd(s)) && touches(s)) out.push(occurrence(appt, s, new Date(s.getTime() + dur), true));
    return true;
  };

  if (repeat === 'weekdays') {
    // Day by day from a little before the window (or the first one), skipping the weekend.
    let d = first;
    const lead = Math.floor((from - first - dur) / DAY_MS) - 2;
    if (lead > 0) d = addDays(first, lead);
    for (let i = 0; i < 400 && d <= to; i++, d = addDays(d, 1)) {
      if (d < first) continue;
      if (d.getDay() === 0 || d.getDay() === 6) continue;
      if (!keep(d)) break;
    }
    return out;
  }
  // Jump close to the window instead of walking from a start years ago.
  let n = Math.max(0, Math.floor((from - first - dur) / (STEP_DAYS[repeat] * DAY_MS)) - 2);
  for (let guard = 0; guard < 500; guard++, n++) {
    const s = nthStart(first, repeat, n);
    if (s === null) continue;
    if (s > to) break;
    if (!keep(s)) break;
  }
  return out;
}

// All appointments' occurrences in [from, to], earliest first. Cancelled ones only when asked.
export function expandAll(appts, from, to, { showCancelled = false } = {}) {
  return appts
    .filter((a) => showCancelled || a.status !== 'cancelled')
    .flatMap((a) => expandSeries(a, from, to))
    .sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
}

// ── Layout ──────────────────────────────────────────────────────────────────────────────────
// All-day, or long enough (24h or more) that Google puts it in the all-day row.
export const isAllDayLike = (o) => !!o.all_day || (o.end - o.start) >= DAY_MS;

// Timed occurrences in one day's column: top/height in minutes from midnight (clipped to the
// day), and col/cols so overlapping ones sit side by side (each cluster shares a column count).
export function layoutDay(occs, day) {
  const d0 = startOfDay(day);
  const d1 = addDays(d0, 1);
  const items = occs
    .filter((o) => o.start < d1 && o.end > d0)
    .map((o) => {
      const s = o.start < d0 ? d0 : o.start;
      const e = o.end > d1 ? d1 : o.end;
      const top = Math.round((s - d0) / 60000);
      return { ...o, top, height: Math.max(1, Math.round((e - s) / 60000)) };
    })
    .sort((a, b) => a.top - b.top || b.height - a.height);
  const out = [];
  let cluster = [];
  let clusterEnd = -1;
  let colEnds = [];
  const flush = () => { const cols = colEnds.length || 1; cluster.forEach((x) => out.push({ ...x, cols })); cluster = []; colEnds = []; };
  for (const it of items) {
    if (it.top >= clusterEnd && cluster.length) flush();
    let col = colEnds.findIndex((end) => end <= it.top);
    if (col === -1) { col = colEnds.length; colEnds.push(0); }
    colEnds[col] = it.top + it.height;
    cluster.push({ ...it, col });
    clusterEnd = Math.max(clusterEnd, it.top + it.height);
  }
  if (cluster.length) flush();
  return out;
}

// All-day bars across a row of days: startIdx/endIdx (inclusive) and a lane, longest first.
// An all-day end is exclusive (the next midnight).
export function laneAllDay(occs, days) {
  if (!days.length) return [];
  const first = startOfDay(days[0]);
  const idx = (d) => Math.round((startOfDay(d) - first) / DAY_MS);
  const last = days.length - 1;
  const bars = occs
    .map((o) => {
      const endDay = o.end.getHours() === 0 && o.end.getMinutes() === 0 && o.end > o.start ? addDays(o.end, -1) : o.end;
      return { ...o, startIdx: Math.max(0, idx(o.start)), endIdx: Math.min(last, idx(endDay)) };
    })
    .filter((b) => b.endIdx >= 0 && b.startIdx <= last && b.endIdx >= b.startIdx)
    .sort((a, b) => a.startIdx - b.startIdx || (b.endIdx - b.startIdx) - (a.endIdx - a.startIdx));
  const laneEnds = [];
  return bars.map((b) => {
    let lane = laneEnds.findIndex((end) => end < b.startIdx);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(-1); }
    laneEnds[lane] = b.endIdx;
    return { ...b, lane };
  });
}

// The same coordinator booked at an overlapping time (cancelled ones don't count).
export function clashesFor(cand, occs) {
  if (!cand.coordinator_id) return [];
  return occs.filter((o) => o.key !== cand.key && o.status !== 'cancelled' && !o.all_day
    && String(o.coordinator_id) === String(cand.coordinator_id) && o.start < cand.end && o.end > cand.start);
}

// ── Reading times ───────────────────────────────────────────────────────────────────────────
const parts = (d) => {
  const h = d.getHours(); const m = d.getMinutes();
  return { h12: h % 12 || 12, m, ap: h < 12 ? 'am' : 'pm' };
};
export function fmtTime(d, { suffix = true } = {}) {
  const p = parts(d);
  return `${p.h12}${p.m ? `:${pad(p.m)}` : ''}${suffix ? p.ap : ''}`;
}
export function fmtRange(s, e) {
  const same = parts(s).ap === parts(e).ap;
  return `${fmtTime(s, { suffix: !same })} – ${fmtTime(e)}`;
}
export const fmtHourLabel = (h) => (h === 0 ? '' : `${h % 12 || 12} ${h < 12 ? 'AM' : 'PM'}`);
export function fmtLongDate(d) {
  return `${WEEKDAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
